import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SITE from "@/constants/site";
import { CellSave, Lightbox, useReveal } from "@/components/site/GalleryPage";
import { ArrowLeft, ArrowRight } from "lucide-react";

// Big serif lines between bands of media — the guests are the story.
const STATEMENTS = [
  "Weekends are expensive. Flights, hotels, time off. You spent all of that on us.",
  "Some of you crossed the country. Some of you crossed town. Every one of you showed up.",
  "Whatever life looked like that week, you set it down and came anyway.",
  "We know what it costs to be somewhere. We noticed.",
  "Every face in these photos is someone we love.",
  "We keep finding new favorites in here.",
  "Thank you for spending your weekend on us.",
];

const BAND_SIZE = 10;
const BANDS_PER_PAGE = 4;

export default function StoryPage() {
  const [clips, setClips] = useState(null);
  const [error, setError] = useState("");
  const [pages, setPages] = useState(1);
  const [lightbox, setLightbox] = useState(-1);
  const sentinelRef = useRef(null);
  const originRect = useRef(null);

  // One Take's album only — no booth strips or originals here
  useEffect(() => {
    fetch("/api/gallery?album=social")
      .then((r) => r.json())
      .then((j) => {
        if (j.error) throw new Error(j.error);
        setClips(j.items);
      })
      .catch((e) => setError(e.message));
  }, []);

  // the reel leads; then the clips run chronologically
  const { reel, bands } = useMemo(() => {
    if (!clips) return { reel: null, bands: [] };
    const reelItem = clips.find((c) => c.id.includes("000_the-reel")) || null;
    const restClips = clips.filter((c) => c !== reelItem);
    const out = [];
    for (let i = 0; i < restClips.length; i += BAND_SIZE) {
      out.push(restClips.slice(i, i + BAND_SIZE));
    }
    return { reel: reelItem, bands: out };
  }, [clips]);

  const visibleBands = bands.slice(0, pages * BANDS_PER_PAGE);
  const flat = useMemo(() => visibleBands.flat(), [visibleBands]);
  const done = visibleBands.length >= bands.length;

  useReveal([flat]);

  // auto-load the next stretch as the sentinel approaches
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || done) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setPages((p) => p + 1)),
      { rootMargin: "1200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [done, flat.length]);

  const step = useCallback(
    (d) => setLightbox((i) => (i + d + flat.length) % flat.length),
    [flat.length]
  );

  return (
    <div className="story">
      <header className="story__nav">
        <Link to="/" className="story__back">
          <ArrowLeft size={16} strokeWidth={1.5} /> {SITE.couple}
        </Link>
        <Link to="/gallery" className="story__back">
          The album <ArrowRight size={16} strokeWidth={1.5} />
        </Link>
      </header>

      <section className="story__open">
        <span className="eyebrow">The Weekend</span>
        <h1 className="story__title font-display">You made this.</h1>
        <p className="story__lede">
          You got on planes and drove for hours. You gave us a whole weekend of
          your lives. Everything below is what you made possible. We will never
          be able to thank you enough.
        </p>
      </section>

      {error && (
        <p className="story__state">The story hit a snag: {error}</p>
      )}
      {!error && clips === null && <p className="story__state">Opening the weekend…</p>}

      {reel && (
        <figure className="story__reel">
          <video
            src={reel.full}
            poster={reel.thumb !== reel.full ? reel.thumb : undefined}
            controls
            playsInline
            preload="metadata"
          />
          <figcaption className="story__reelcap">
            The weekend in forty seconds, by One Take Bridal
          </figcaption>
        </figure>
      )}

      {visibleBands.map((band, bi) => (
        <React.Fragment key={bi}>
          {bi % 2 === 0 && STATEMENTS[bi / 2] && (
            <p className="story__statement font-display">{STATEMENTS[bi / 2]}</p>
          )}
          <div className="ggrid story__band">
            {band.map((item) => {
              const idx = flat.indexOf(item);
              return (
                <div key={item.id} className="ggrid__cell">
                  <button
                    type="button"
                    className="ggrid__open"
                    aria-label="View photo"
                    onClick={(e) => {
                      originRect.current = e.currentTarget.closest(".ggrid__cell").getBoundingClientRect();
                      setLightbox(idx);
                    }}
                  >
                    {item.kind === "video" && item.thumb === item.full ? (
                      <video className="ggrid__media" src={`${item.thumb}#t=0.1`} preload="metadata" muted playsInline />
                    ) : (
                      <img className="ggrid__media" src={item.thumb} alt="" loading="lazy" />
                    )}
                    {item.kind === "video" && <span className="ggrid__play" aria-hidden="true">▶</span>}
                  </button>
                  <CellSave item={item} />
                </div>
              );
            })}
          </div>
        </React.Fragment>
      ))}

      {!done && <div ref={sentinelRef} className="story__state">…</div>}

      {done && clips !== null && (
        <section className="story__close">
          <p className="story__statement font-display">
            A weekend is not a small thing to give someone. You gave us yours,
            no matter where life had you. It didn&rsquo;t go unnoticed. It
            never will.
          </p>
          <p className="story__sign font-script">{SITE.couple}</p>
          <div className="story__actions">
            <Link className="story__cta" to="/gallery">Find your photos</Link>
            <Link className="story__cta story__cta--ghost" to="/#credits">The credits</Link>
          </div>
        </section>
      )}

      {lightbox >= 0 && (
        <Lightbox items={flat} index={lightbox} origin={originRect.current} onClose={() => setLightbox(-1)} onStep={step} />
      )}
    </div>
  );
}
